import { getLang } from '../i18n';
import { logEvent } from './debugLog';
import { loadConfig } from './configService';
import type { Vec3 } from '../types';

/**
 * Client for the `concierge` Edge Function — natural-language venue Q&A
 * grounded in the visitor's live position. Returns a short reply plus an
 * optional poiId/tourId when the answer implies "go there".
 * Engine: Claude (server-side, when ANTHROPIC_API_KEY is configured) with a
 * deterministic fuzzy-search fallback — the UI treats both identically.
 */

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL as string | undefined;

export interface ConciergeReply {
  reply: string;
  poiId?: string;
  tourId?: string;
  /** Multi-stop route (v2 agent): POI ids in visit order. */
  poiIds?: string[];
  /** v8: the agent extracted a new-POI bookmark from conversation — client
   *  should arm tap-to-place so the visitor can pin the exact spot. */
  suggestPoi?: { name: string; category?: string; description?: string };
  source: 'llm' | 'lite';
  /** v3: WHY the LLM path didn't run (lite fallbacks only). */
  reason?: string;
}

export interface ConciergeTurn {
  role: 'user' | 'assistant';
  content: string;
}

export function isConciergeAvailable(): boolean {
  return Boolean(SUPABASE_URL);
}

export async function askConcierge(
  message: string,
  position: Vec3 | null,
  history: ConciergeTurn[] = [],
): Promise<ConciergeReply> {
  if (!SUPABASE_URL) throw new Error('Concierge unavailable — database not configured');
  const res = await fetch(`${SUPABASE_URL}/functions/v1/concierge`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    // history = conversation memory (last few turns) so follow-ups like
    // "and after that?" resolve against what was already said. mapCode
    // lets the function look up this venue's chosen AI provider
    // (venue_settings.concierge_provider, Dashboard-configurable).
    body: JSON.stringify({ message, lang: getLang(), position, history: history.slice(-8), mapCode: loadConfig().code }),
  });
  if (!res.ok) throw new Error(`Concierge error (HTTP ${res.status})`);
  const data = (await res.json()) as ConciergeReply;
  logEvent(
    'session',
    `concierge (${data.source}): "${message.slice(0, 40)}" → ${
      data.poiIds ? `route[${data.poiIds.length}]` : data.poiId ?? data.tourId ?? 'answer only'
    }`,
  );
  // Lite fallback with a reason = the agent WANTED to run but couldn't —
  // put the why in the debug report (missing/invalid API key, HTTP error…).
  if (data.reason) logEvent('error', `concierge lite fallback: ${data.reason}`);
  return data;
}
